import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";

const TOTAL_SLOTS = 30;

function SlotsConfig() {
  const [machines, setMachines] = useState([]);
  const [machineId, setMachineId] = useState("");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [bulkPrice, setBulkPrice] = useState("1500");

  const loadMachines = async () => {
    const res = await API.get("/machines");
    setMachines(res.data || []);
  };

  const loadSlots = async id => {
    if (!id) {
      setSlots([]);
      return;
    }
    setLoading(true);
    try {
      const res = await API.get(`/stock/${id}`);
      setSlots(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMachines();
  }, []);

  const slotMap = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      map.set(String(s.slot_code), s);
    }
    return map;
  }, [slots]);

  const rows = useMemo(() => {
    return Array.from({ length: TOTAL_SLOTS }, (_, i) => {
      const code = String(i + 1);
      const existing = slotMap.get(code);
      return {
        code,
        id: existing?.id || null,
        productName: existing?.product_name || `Slot ${code}`,
        price: existing?.price ?? 1500,
        stock: existing?.stock ?? 0
      };
    });
  }, [slotMap]);

  const seedDefaults = async () => {
    if (!machineId) return;
    await API.post("/stock/seed-default", {
      machine_id: Number(machineId),
      slots_count: TOTAL_SLOTS,
      price: 1500,
      stock: 0
    });
    await loadSlots(machineId);
  };

  const applyBulkPrice = async () => {
    if (!machineId) return;
    const p = Number(bulkPrice);
    if (!Number.isInteger(p) || p < 0) {
      alert("Precio inválido");
      return;
    }
    for (const r of rows) {
      if (!r.id) continue;
      await API.put(`/stock/${r.id}`, { price: p });
    }
    await loadSlots(machineId);
  };

  const saveRow = async row => {
    if (!machineId) return;
    const price = Number(row.price);
    const stock = Number(row.stock);
    if (!Number.isInteger(price) || price < 0) {
      alert(`Precio inválido en slot ${row.code}`);
      return;
    }
    if (!Number.isInteger(stock) || stock < 0) {
      alert(`Stock inválido en slot ${row.code}`);
      return;
    }
    setSavingId(row.code);
    try {
      if (row.id) {
        await API.put(`/stock/${row.id}`, {
          stock,
          price
        });
      } else {
        await API.post("/stock", {
          machine_id: Number(machineId),
          slot_code: row.code,
          product_name: row.productName,
          price,
          stock
        });
      }
      await loadSlots(machineId);
    } catch (e) {
      alert(e?.response?.data?.error || `Error guardando slot ${row.code}`);
    } finally {
      setSavingId(null);
    }
  };

  const [drafts, setDrafts] = useState({});
  useEffect(() => {
    const next = {};
    for (const r of rows) {
      next[r.code] = { ...r };
    }
    setDrafts(next);
  }, [rows]);

  const updateDraft = (code, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        [field]: value
      }
    }));
  };

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Configuración de Slots</h1>
        <p className="page-subtitle">
          Define los 30 slots por máquina (precio, stock y nombre). Este panel queda para ajustes
          futuros de operación.
        </p>
      </header>

      <div className="card card--padded" style={{ marginBottom: "1rem" }}>
        <div className="field-row">
          <select
            className="select"
            value={machineId}
            onChange={e => {
              const v = e.target.value;
              setMachineId(v);
              loadSlots(v);
            }}
          >
            <option value="">Seleccionar máquina</option>
            {machines.map(m => (
              <option key={m.id} value={m.id}>
                {m.name} (id {m.id})
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-secondary" disabled={!machineId} onClick={seedDefaults}>
            Crear/asegurar 30 slots a $1500
          </button>
          <input
            className="input"
            type="number"
            value={bulkPrice}
            onChange={e => setBulkPrice(e.target.value)}
            style={{ maxWidth: "130px" }}
          />
          <button type="button" className="btn btn-primary" disabled={!machineId} onClick={applyBulkPrice}>
            Aplicar precio a todos
          </button>
        </div>
      </div>

      {machineId && (
        <div className="table-wrap">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Slot</th>
                <th>Producto</th>
                <th>Precio CLP</th>
                <th>Stock</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>Cargando...</td>
                </tr>
              ) : (
                rows.map(r => {
                  const d = drafts[r.code] || r;
                  return (
                    <tr key={r.code}>
                      <td><strong>{r.code}</strong></td>
                      <td>
                        <input
                          className="input"
                          value={d.productName}
                          onChange={e => updateDraft(r.code, "productName", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          value={d.price}
                          onChange={e => updateDraft(r.code, "price", Number(e.target.value))}
                          style={{ maxWidth: "120px" }}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          value={d.stock}
                          onChange={e => updateDraft(r.code, "stock", Number(e.target.value))}
                          style={{ maxWidth: "100px" }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => saveRow(d)}
                          disabled={savingId === r.code}
                        >
                          {savingId === r.code ? "Guardando..." : "Guardar"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SlotsConfig;
